<template>
  <div class="contents">
    <div class="container-fluid">
      <div class="breadcrumb-main user-member justify-content-sm-between mb-3">
        <h4 class="text-capitalize fw-500 breadcrumb-title">💰 Saldo Toko</h4>
        <button class="btn btn-success text-white" @click="openWithdraw" :disabled="!balance || balance.balance < minWithdraw">
          <i class="fas fa-money-bill-wave"></i> Tarik Saldo
        </button>
      </div>

      <div class="row" v-if="balance">
        <div class="col-md-3">
          <div class="card p-3 mb-3 border bg-white radius-xl">
            <small class="text-muted">Saldo Available</small>
            <h3 class="mb-0 text-success">{{ formatRupiah(balance.balance) }}</h3>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card p-3 mb-3 border bg-white radius-xl">
            <small class="text-muted">Pending Withdrawal</small>
            <h3 class="mb-0 text-warning">{{ formatRupiah(balance.pending_withdrawal) }}</h3>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card p-3 mb-3 border bg-white radius-xl">
            <small class="text-muted">Total Earned</small>
            <h3 class="mb-0">{{ formatRupiah(balance.total_earned) }}</h3>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card p-3 mb-3 border bg-white radius-xl">
            <small class="text-muted">Total Withdrawn</small>
            <h3 class="mb-0">{{ formatRupiah(balance.total_withdrawn) }}</h3>
          </div>
        </div>
      </div>

      <div class="alert alert-info">
        <div><b>Saldo toko hanya dari pembayaran Tripay</b> (QRIS, VA, e-wallet). Transfer manual &amp; COD tidak masuk saldo.</div>
        <div class="mt-1">Fee platform: <b>{{ formatRupiah(transactionFee) }}</b>/transaksi · Fee Tripay: sesuai channel · Min withdraw: <b>{{ formatRupiah(minWithdraw) }}</b> · Fee withdraw: <b>{{ formatRupiah(withdrawFee) }}</b></div>
      </div>

      <ul class="nav nav-tabs mb-3">
        <li class="nav-item"><a class="nav-link" :class="{active: tab==='tx'}" @click="tab='tx'" style="cursor:pointer">History Transaksi</a></li>
        <li class="nav-item"><a class="nav-link" :class="{active: tab==='wd'}" @click="tab='wd'" style="cursor:pointer">History Withdraw</a></li>
      </ul>

      <div v-if="tab==='tx'" class="card p-3 border bg-white radius-xl">
        <table class="table table-sm">
          <thead>
            <tr>
              <th>Waktu</th>
              <th>Tipe</th>
              <th>Gross</th>
              <th>Tripay Fee</th>
              <th>Platform Fee</th>
              <th>Net</th>
              <th>Keterangan</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="t in transactions" :key="t.id">
              <td><small>{{ formatTime(t.created_at) }}</small></td>
              <td>
                <span class="badge" :class="typeBadge(t.type)">{{ t.type }}</span>
              </td>
              <td>
                <span v-if="t.gross_amount">{{ formatRupiah(t.gross_amount) }}</span>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-danger">
                <span v-if="t.tripay_fee">− {{ formatRupiah(t.tripay_fee) }}</span>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-danger">
                <span v-if="t.platform_fee">− {{ formatRupiah(t.platform_fee) }}</span>
                <span v-else class="text-muted">—</span>
              </td>
              <td>
                <b :class="t.type === 'credit' ? 'text-success' : 'text-danger'">
                  {{ t.type === 'credit' ? '+' : '−' }} {{ formatRupiah(t.net_amount || t.amount) }}
                </b>
              </td>
              <td><small>{{ t.description }}</small></td>
            </tr>
            <tr v-if="transactions.length === 0"><td colspan="7" class="text-center text-muted">Belum ada transaksi</td></tr>
          </tbody>
        </table>
      </div>

      <div v-if="tab==='wd'" class="card p-3 border bg-white radius-xl">
        <table class="table table-sm">
          <thead><tr><th>Waktu</th><th>Saldo Dikunci</th><th>Fee</th><th>Diterima</th><th>Bank</th><th>Status</th></tr></thead>
          <tbody>
            <tr v-for="w in withdrawals" :key="w.id">
              <td><small>{{ formatTime(w.created_at) }}</small></td>
              <td><b>{{ formatRupiah(w.amount) }}</b></td>
              <td class="text-danger">− {{ formatRupiah(w.withdraw_fee || 0) }}</td>
              <td class="text-success"><b>{{ formatRupiah(w.net_transfer_amount || (w.amount - (w.withdraw_fee||0))) }}</b></td>
              <td>{{ w.bank_name }} {{ w.bank_account_number }}<br><small>{{ w.bank_account_holder }}</small></td>
              <td>
                <span class="badge" :class="wdBadge(w.status)">{{ w.status }}</span>
                <br><small v-if="w.admin_notes" class="text-muted">{{ w.admin_notes }}</small>
              </td>
            </tr>
            <tr v-if="withdrawals.length === 0"><td colspan="6" class="text-center text-muted">Belum ada withdraw</td></tr>
          </tbody>
        </table>
      </div>

      <!-- Withdraw modal -->
      <modal name="withdraw_modal" :height="'auto'" :adaptive="true">
        <div class="p-4">
          <h4>Tarik Saldo</h4>
          <form @submit.prevent="submitWithdraw">
            <label>Jumlah dari saldo (min {{ formatRupiah(minWithdraw) }})</label>
            <input v-model.number="form.amount" type="number" class="form-control mb-2" required @input="recalcWithdraw">
            <div class="alert alert-light border mb-2" v-if="form.amount >= minWithdraw">
              <div>Fee withdraw: <b class="text-danger">− {{ formatRupiah(withdrawFee) }}</b></div>
              <div>Diterima ke rekening: <b class="text-success">{{ formatRupiah(netWithdrawPreview) }}</b></div>
            </div>
            <label>Bank</label>
            <input v-model="form.bank_name" class="form-control mb-2" placeholder="BCA / Mandiri / BNI" required>
            <label>Nomor Rekening</label>
            <input v-model="form.bank_account_number" class="form-control mb-2" required>
            <label>Atas Nama</label>
            <input v-model="form.bank_account_holder" class="form-control mb-3" required>
            <button type="submit" class="btn btn-primary" :disabled="submitting || netWithdrawPreview <= 0">
              {{ submitting ? 'Submitting...' : 'Submit Withdrawal' }}
            </button>
            <button type="button" class="btn btn-light ml-2" @click="$modal.hide('withdraw_modal')">Batal</button>
          </form>
        </div>
      </modal>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
export default {
  data() {
    return {
      tab: 'tx',
      balance: null,
      transactions: [],
      withdrawals: [],
      minWithdraw: 100000,
      transactionFee: 5000,
      withdrawFee: 6500,
      submitting: false,
      form: { amount: 100000, bank_name: '', bank_account_number: '', bank_account_holder: '' },
    };
  },
  computed: {
    netWithdrawPreview() {
      return Math.max(0, (this.form.amount || 0) - this.withdrawFee);
    },
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      try {
        const r1 = await this.$store.dispatch('shop_balance/shop_balance_get');
        this.balance = r1.data.data.balance;
        this.minWithdraw = r1.data.data.min_withdrawal;
        this.transactionFee = r1.data.data.transaction_fee || 5000;
        this.withdrawFee = r1.data.data.withdraw_fee || 6500;

        await this.$store.dispatch('shop_balance/shop_balance_transactions', { limit: 50 });
        this.transactions = this.$store.state.shop_balance.transactions;

        await this.$store.dispatch('shop_balance/shop_balance_my_withdrawals', { limit: 20 });
        this.withdrawals = this.$store.state.shop_balance.withdrawals;
      } catch (e) { this.$toast.error('Gagal load saldo'); }
    },
    recalcWithdraw() {},
    openWithdraw() {
      this.form = { amount: this.minWithdraw, bank_name: '', bank_account_number: '', bank_account_holder: '' };
      this.$modal.show('withdraw_modal');
    },
    async submitWithdraw() {
      this.submitting = true;
      try {
        const r = await this.$store.dispatch('shop_balance/shop_balance_withdraw', this.form);
        this.$toast.success(r.data.message);
        this.$modal.hide('withdraw_modal');
        this.load();
      } catch (e) { this.$toast.error(e.response?.data?.message || 'Gagal'); }
      this.submitting = false;
    },
    formatRupiah(v) { return 'Rp ' + (v || 0).toLocaleString('id-ID'); },
    formatTime(t) { return t ? moment(parseInt(t)).format('DD MMM YYYY HH:mm') : '-'; },
    typeBadge(t) {
      return { 'badge-success': t === 'credit', 'badge-danger': t === 'debit',
        'badge-warning': t === 'lock', 'badge-info': t === 'fee' };
    },
    wdBadge(s) {
      return { 'badge-warning': s === 'pending', 'badge-info': s === 'approved',
        'badge-success': s === 'completed', 'badge-danger': s === 'rejected' };
    },
  },
};
</script>
